/**
 * Wishlist — Prestige Theme
 * Przechowywanie: localStorage (klucz: wl_products)
 */

window.Wishlist = (() => {
  const LS_KEY = 'wl_products';

  /* ─── localStorage ─── */
  const getItems = ()    => JSON.parse(localStorage.getItem(LS_KEY) || '[]');
  const setItems = (arr) => localStorage.setItem(LS_KEY, JSON.stringify(arr));

  async function toggle(handle) {
    const items = getItems();
    const exist = items.includes(handle);
    const next  = exist ? items.filter(i => i !== handle) : [...items, handle];

    setItems(next);

    document.dispatchEvent(new CustomEvent('wishlist:change', {
      detail: { handle, added: !exist, items: next }
    }));
    return !exist;
  }

  function getCount() {
    return getItems().length;
  }

  /* ─── UI: przyciski ♥ ─── */
  function initButtons() {
    const items = getItems();
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const handle = btn.dataset.productHandle;
      if (!handle) return;
      setBtn(btn, items.includes(handle));
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        btn.disabled = true;
        const added = await toggle(handle);
        setBtn(btn, added);
        btn.disabled = false;
        toast(added
          ? (window.__wl_i18n?.added   || 'Dodano do listy życzeń')
          : (window.__wl_i18n?.removed || 'Usunięto z listy życzeń')
        );
        updateCounter();
      });
    });
  }

  function setBtn(btn, active) {
    btn.classList.toggle('wishlist-btn--active', active);
    btn.setAttribute('aria-pressed', String(active));
  }

  /* ─── Toast ─── */
  function toast(msg) {
    let el = document.getElementById('wishlist-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'wishlist-toast';
      el.className = 'wishlist-toast';
      document.body.appendChild(el);
    }
    el.textContent = msg;
    el.classList.add('wishlist-toast--visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('wishlist-toast--visible'), 2500);
  }

  /* ─── Licznik ─── */
  function updateCounter() {
    const n = getCount();
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = n;
      el.style.display = n > 0 ? '' : 'none';
    });
  }

  /* ─── Pobierz dane produktu ─── */
  async function fetchProduct(handle) {
    try {
      const r = await fetch(`/products/${handle}.js`);
      if (!r.ok) return null;
      return r.json();
    } catch { return null; }
  }

  /* ─── Drawer ─── */
  function getDrawer() {
    return document.getElementById('wishlist-drawer');
  }

  async function openDrawer() {
    const drawer = getDrawer();
    if (!drawer) return;
    await renderDrawer();
    drawer.show?.() ?? drawer.setAttribute('open', '');
  }

  async function renderDrawer() {
    const inner = document.getElementById('wishlist-drawer-inner');
    if (!inner) return;

    const handles = getItems();

    if (!handles.length) {
      inner.innerHTML = `<p class="h5 text-center">${window.__wl_i18n?.empty || 'Twoja lista życzeń jest pusta.'}</p>`;
      return;
    }

    inner.innerHTML = `<p class="text-center">${window.__wl_i18n?.loading || 'Ładowanie...'}</p>`;

    const products = (await Promise.all(handles.map(fetchProduct))).filter(Boolean);

    if (!products.length) {
      inner.innerHTML = `<p class="h5 text-center">${window.__wl_i18n?.empty || 'Brak produktów.'}</p>`;
      return;
    }

    const currency = window.Shopify?.currency?.active || 'PLN';

    inner.innerHTML = products.map(p => {
      const fmt      = new Intl.NumberFormat('pl-PL', { style: 'currency', currency }).format(p.price / 100);
      const img      = p.featured_image || '';
      const variant  = p.variants?.[0];
      const hasOnlyDefault = p.variants?.length === 1 && variant?.title === 'Default Title';
      const variantTitle   = (!hasOnlyDefault && variant?.title) ? `<p class="smallcaps text-subdued">${variant.title}</p>` : '';

      return `<line-item class="line-item" style="position:relative">
        ${img ? `<div style="position:relative;flex-shrink:0;align-self:flex-start">
          <img src="${img}" alt="${p.title}" loading="lazy" class="line-item__media" style="border-radius:0;display:block">
          <button
            class="wishlist-drawer-remove"
            data-remove-handle="${p.handle}"
            type="button"
            aria-label="Usuń z listy życzeń"
            style="position:absolute;top:4px;left:4px"
          >
            <svg aria-hidden="true" focusable="false" width="20" viewBox="0 0 20 20">
              <path d="M15.8334 11.6667C17.0751 10.45 18.3334 8.99167 18.3334 7.08333C18.3334 5.86776 17.8505 4.70197 16.991 3.84243C16.1314 2.98289 14.9657 2.5 13.7501 2.5C12.2834 2.5 11.2501 2.91667 10.0001 4.16667C8.75008 2.91667 7.71675 2.5 6.25008 2.5C5.03451 2.5 3.86872 2.98289 3.00918 3.84243C2.14963 4.70197 1.66675 5.86776 1.66675 7.08333C1.66675 9 2.91675 10.4583 4.16675 11.6667L10.0001 17.5L15.8334 11.6667Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>` : ''}
        <div class="line-item-info">
          <div class="v-stack justify-items-start gap-2">
            <div class="v-stack justify-items-start gap-1">
              <a href="/products/${p.handle}">${p.title}</a>
              <span class="price">${fmt}</span>
            </div>
            ${variantTitle ? `<div class="v-stack justify-items-start gap-1">${variantTitle}</div>` : ''}
            <div class="h-stack gap-4">
              ${!p.available
                ? `<span class="text-subdued text-xs">${window.__wl_i18n?.sold_out || 'Wyprzedany'}</span>`
                : `<a href="/products/${p.handle}" class="link text-xs">${window.__wl_i18n?.add_to_cart || 'Dodaj do koszyka'}</a>`}
            </div>
          </div>
        </div>
      </line-item>`;
    }).join('');

    inner.querySelectorAll('[data-remove-handle]').forEach(btn => {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        await toggle(btn.dataset.removeHandle);
        await renderDrawer();
        updateCounter();
      });
    });
  }

  /* ─── Trigger drawera ─── */
  function initDrawerTriggers() {
    document.querySelectorAll('[data-wishlist-drawer-trigger]').forEach(el => {
      el.addEventListener('click', e => {
        e.preventDefault();
        openDrawer();
      });
    });
  }

  /* ─── Bootstrap ─── */
  function init() {
    initButtons();
    updateCounter();
    initDrawerTriggers();
    document.addEventListener('wishlist:change', () => updateCounter());
  }

  return { init, toggle, getItems, getCount, openDrawer };
})();

document.addEventListener('DOMContentLoaded', () => Wishlist.init());

/* ─── WishlistDrawer: własny element bez auto-open przy cart:change ─── */
(function () {
  function registerWishlistDrawer() {
    const CartDrawerClass = customElements.get('cart-drawer');
    if (!CartDrawerClass) {
      requestAnimationFrame(registerWishlistDrawer);
      return;
    }

    const DrawerClass = Object.getPrototypeOf(CartDrawerClass);

    class WishlistDrawer extends DrawerClass {
      connectedCallback() {
        super.connectedCallback();
      }
    }

    customElements.define('wishlist-drawer', WishlistDrawer);
  }

  registerWishlistDrawer();
})();